<template>
  <page-outline :title="$t('Webheader.RSVcreneau')">
    <template slot="pageContent">
      <hr class="mx-auto" />
      <form-accordion :title="$t('Webheader.choose_slot')" :isTitleUpperCase="false">
        <div class="container">
          <!-- Navigation du calendrier -->
          <div class="calendar-navigation">
            <button @click="previousYear">&#x3C;</button>
            <span class="calendar-year">{{ currentYear }}</span>
            <button @click="nextYear">&#x3E;</button>
          </div>
          <div class="calendar-container">
            <table class="calendar-table">
              <thead>
                <tr>
                  <th class="month-header">{{ $t('calendar.month') }}</th>
                  <th v-for="day in 31" :key="day" class="day-header">{{ day }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(days, monthKey, index) in calendarData" :key="monthKey">
                  <td class="month-label">{{ $t(`calendar.months.${monthKeys[index]}`) }}</td>
                  <td v-for="(date, idx) in days" :key="idx"
                    :class="{
                      'out-of-month': date.isOutOfMonth,
                      'current-session': date.isInCurrentSession,
                      'selected': date.isSelected,
                      'weekend': date.isWeekend && !date.isInCurrentSession && !date.isSelected,
                      'non-bookable': date.isNonBookable && !(date.isWeekend && (date.isInCurrentSession || date.isSelected)),
                      'clickable': !date.isNonBookable && !date.isOutOfMonth && !date.isWeekend
                      }"
                      @click="!date.isInCurrentSession && selectDate(date)" :title="date.dayName">
                    <span v-if="date.isSelected">{{ date.isInCurrentSession || date.isSelected ? date.dayName : date.day }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="calendar-legend">
            <div class="legend-item">
              <div class="legend-color disabled"></div>
              <span>{{ $t('calendar.legend_unavailable') }}</span>
            </div>
            <div class="legend-item">
              <div class="legend-color clickable"></div>
              <span>{{ $t('calendar.legend_available') }}</span>
            </div>
            <div class="legend-item">
              <div class="legend-color current-session"></div>
              <span>{{ $t('calendar.legend_current') }}</span>
            </div>
          </div>
          <div v-if="selectedDate" class="button-group">
            <b-button class="btnFilter_2 mx-3"
              @click="confirmReservation" variant="primary">
              {{ $t('calendar.confirm') }}
            </b-button>
            <b-button class="btnFilter_2"
              @click="cancelReservation">
              {{ $t('calendar.cancel') }}
            </b-button>
          </div>
        </div>
      </form-accordion>
    </template>
  </page-outline>
</template>

<script>
import BackButton from "@/components/common/backButton.vue"
import PageOutline from "@/components/common/PageOutline.vue";
import Spinner from "@/components/common/spinner.vue";
import formAccordion from "@/components/common/FormAccordion.vue"
import {errorMessageAlert} from "@/helpers/alert"
import {loadPage} from '@/helpers/routing'

export default {
  name: "BookPage",
  components: {
    "form-accordion": formAccordion,
    BackButton,
    "page-outline": PageOutline,
    spinner: Spinner,
  },
  data() {
    return {
      currentLocale: this.$i18n.locale || 'fr',
      selectedDate: null,
      events: [],
      currentSessionDates: [],
      Books: [],
      daysInMonth: Array.from({ length: 31 }, (_, i) => i + 1),
      monthKeys: [
        "january", "february", "march", "april", "may", "june",
        "july", "august", "september", "october", "november", "december"
      ],
      activeDates: [],
      calendarData: {},
      currentYear: new Date().getFullYear(),
      today: new Date(),
      timezone: '+00:00'
    };
  },
  watch: {
    '$i18n.locale'(newLang) {
      this.currentLocale = newLang;
      this.generateCalendar();
    }
  },
  created () {
    this.timezone = moment.tz.guess()
    this.getAllBookingAndDisableDates();

  },
  methods: {
    getAllBookingAndDisableDates(filters = {}) {
      this.$engine.firstOn().planningTB.getAllBooking(filters)
      .then(resp => {
        const bookings = resp.data.books || [];
        const current = resp.data.sessionEnCours || [];
        // sessions futures
        this.activeDates = bookings.map(session => ({
          start: new Date(session.DateStart),
          end: new Date(session.DateEnd)
        }));
        // sessions déjà prises par l'utilisateur
        this.currentSessionDates = current.map(session => ({
          start: new Date(session.DateStart),
          end: new Date(session.DateEnd)
        }));
        this.generateCalendar();
      })
      .catch(error => {
        errorMessageAlert(error, this);
      });
    },
    generateCalendar() {
      this.calendarData = {};
      const weekdays = this.$t('calendar.weekdays_short');
      this.monthKeys.forEach((monthKey, index) => {
        let daysInMonth = new Date(this.currentYear, index + 1, 0).getDate();
        this.calendarData[monthKey] = Array.from({ length: 31 }, (_, day) => {
          let currentDate = new Date(this.currentYear, index, day + 1);
          let dayOfWeek = currentDate.getDay();
          const isOutOfMonth = day + 1 > daysInMonth;
          const isPast = currentDate < new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate());
          const isWeekend = (dayOfWeek === 0 || dayOfWeek === 6) && !isOutOfMonth;
          const isStartDate = this.activeDates.some(d =>
            d.start.getFullYear() === currentDate.getFullYear() &&
            d.start.getMonth() === currentDate.getMonth() &&
            d.start.getDate() === currentDate.getDate()
          );
          const isInCurrentSession = this.currentSessionDates.some(d =>
            currentDate >= d.start && currentDate <= d.end
          );

          const isWeekendInSession = isWeekend && this.activeDates.some(d =>
            currentDate >= d.start && currentDate <= d.end
          );
          const isNonBookable = (!isWeekend && !isStartDate && !isOutOfMonth && !isInCurrentSession)
            || (!isWeekend && isPast && !isOutOfMonth && !isInCurrentSession);
          return {
            day: day + 1,
            isOutOfMonth,
            isWeekend,
            isNonBookable,
            isStartDate,
            isSelected: false,
            isInCurrentSession,
            isWeekendInSession,
            dayName: weekdays[dayOfWeek],
            dateObj: currentDate
          };
        });
      });
    },
    selectDate(date) {
      if (!date.isNonBookable && date.isStartDate && !date.isOutOfMonth && !date.isWeekend) {

        // Reset des sélections
        Object.values(this.calendarData).forEach(month =>
          month.forEach(day => day.isSelected = false)
        );
        let selectedRange = this.activeDates.find(d =>
          d.start.getFullYear() === this.currentYear &&
          d.start.getMonth() === date.dateObj.getMonth() &&
          d.start.getDate() === date.day
        );
        if (selectedRange) {
          let currentDate = new Date(selectedRange.start);
          while (currentDate <= selectedRange.end) {
            const monthKey = this.monthKeys[currentDate.getMonth()];
            const dayIndex = currentDate.getDate() - 1;
            if (this.calendarData[monthKey] && this.calendarData[monthKey][dayIndex]) {
              const dayObj = this.calendarData[monthKey][dayIndex];
              if (!dayObj.isOutOfMonth) {
                dayObj.isSelected = true;
              }
            }
            currentDate.setDate(currentDate.getDate() + 1);
          }
          this.selectedDate = date;
        }
      }
    },
    confirmReservation() {
      const selectedRange = this.activeDates.find(d =>
        d.start.getFullYear() === this.currentYear &&
        d.start.getMonth() === this.selectedDate.dateObj.getMonth() &&
        d.start.getDate() === this.selectedDate.day
      );
      if (selectedRange) {
        const startDate = selectedRange.start;
        const endDate = selectedRange.end;
        const formatDateTime = (date) => {
          const day = String(date.getDate()).padStart(2, '0');
          const month = this.$t(`calendar.months.${this.monthKeys[date.getMonth()]}`);
          const year = date.getFullYear();
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${day} ${month}, ${year} ${hours}h${minutes}`;
        };
        const formattedStart = formatDateTime(startDate);
        const formattedEnd = formatDateTime(endDate);
        const confirmationText = this.$t('calendar.confirmation', {
          start: formattedStart,
          end: formattedEnd
        });
        let dateSlot = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
        let params = { dateSlot: dateSlot };
        this.$engine.firstOn().planningTB.addBooking(params)
          .then(resp => {
            this.$f7.modal({
              title: this.$t('common.planningTB_added'),
              text: confirmationText,
              buttons: [{
                text: 'OK',
                onClick: () => {
                  this.selectedDate = null;
                  this.getAllBookingAndDisableDates();
                }
              }]
            });
          })
          .catch((e) => {
            errorMessageAlert(e, this);
          });
      }
    },
    cancelReservation() {
      if (this.selectedDate) {
        this.selectedDate = null;
        Object.values(this.calendarData).forEach(month =>
          month.forEach(day => day.isSelected = false)
        );
      }
    },
    previousYear() {
      this.currentYear--;
      this.generateCalendar();
    },
    nextYear() {
      this.currentYear++;
      this.generateCalendar();
    },
    getDate(date){
      if (!date || date == ''){
        return '-'
      }
      const hasTimezone = /([+-]\d{2}:\d{2}|Z)$/.test(date);
      const isoWithTZ = hasTimezone ? date : date + 'Z';
      return moment(isoWithTZ).tz(this.timezone).format('DD/MM/YYYY HH:mm:ss');
    },
  },
};
</script>
<style scoped>
/* === Général === */
.container {
  max-width: 2000px;
  margin: auto;
  text-align: center;
  padding: 20px;
}
.calendar-navigation {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  font-size: 20px;
  margin-bottom: 10px;
}
.calendar-container {
  display: block;
  overflow-x: auto;
  max-width: 100%;
  background: #fff;
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}
/* === Table calendrier === */
.calendar-table {
  border-collapse: collapse;
  min-width: 1000px;
  width: 100%;
}
.calendar-table th,
.calendar-table td {
  border: 1px solid #ddd;
  padding: 12px;
  text-align: center;
  min-width: 40px;
  height: 40px;
  transition: all 0.2s ease;
  vertical-align: middle;
}
.calendar-year {
  font-size: 28px !important;
  font-weight: bold;
  margin: 0 10px;
  color: #007676;
}
/* Sticky Headers */
.month-header,
.month-label,
.day-header {
  background: #fff;
  font-weight: bold;
  z-index: 5;
}
.month-header {
  position: sticky;
  top: 0;
  left: -12px;
  padding: 10px;
  min-width: 150px;
  max-width: 150px;
  border-right: 2px solid #ddd;
  box-shadow: 3px 0 5px rgba(0, 0, 0, 0.05);
}
.month-label {
  position: sticky;
  left: -12px;
  padding-left: 10px;
  min-width: 150px;
  max-width: 150px;
  border-right: 2px solid #ddd;
  box-shadow: 3px 0 5px rgba(0, 0, 0, 0.05);
}
.day-header {
  position: sticky;
  top: 0;
  min-width: 40px;
  height: 40px;
}
/* === Cellules === */
.current-session {
  position: relative;
  background-color: #005353 !important;
  color: white !important;
  font-weight: bold;
  cursor: not-allowed;
  overflow: hidden;
}
.current-session::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 200%;
  height: 200%;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.2),
    rgba(255, 255, 255, 0.2) 4px,
    transparent 4px,
    transparent 8px
  );
  z-index: 1;
  pointer-events: none;
}
.weekend,
.out-of-month {
  background-color: #e8e8e8 !important;
  color: #aaa !important;
  pointer-events: none;
}
.non-bookable {
  background-color: #f5f5f5 !important;
  color: #aaa !important;
  pointer-events: none;
}
.clickable {
  background-color: #0f9898;
  cursor: pointer;
  color: #fff;
  transition: all 0.2s ease;
}
.clickable:hover {
  background-color: #007676;
}
.selected {
  background-color: #007676 !important;
  color: white;
  font-weight: bold;
  border-radius: 5px;
}
.selected span {
  font-size: 11px;
  display: inline-block;
  width: 100%;
  text-align: center;
}
/* === Légende === */
.calendar-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 15px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #ccc;
}
.legend-color.disabled {
  background: linear-gradient(135deg, #f5f5f5 50%, #e0e0e0 50%);
  border: 1px solid #ccc;
  width: 20px;
  height: 20px;
  border-radius: 4px;
}
.legend-color.clickable {
  background-color: #0f9898;
}
/* === Boutons === */
.button-group {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 30px;
}
/* === Responsive === */
@media screen and (max-width: 768px) {
  .container {
    padding: 10px;
    max-width: 95vw;
  }
  .calendar-navigation {
    flex-direction: column;
    font-size: 18px;
    gap: 10px;
  }
  .calendar-container {
    padding: 5px;
  }
  .calendar-table {
    min-width: 1000px;
  }
  .calendar-table th,
  .calendar-table td {
    font-size: 12px;
    padding: 5px;
  }
}
</style>
